import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import { Plus, Trash2, Edit2, Key, CheckCircle, XCircle } from 'lucide-react';
import { XiaohongshuIcon, DouyinIcon } from '../components/PlatformIcons';

const AccountCard = ({ account, onEdit, onDelete }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between hover:shadow-md transition-all group">
    <div className="flex justify-between items-start mb-4">
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-full p-2">
          {account.platform === 'xiaohongshu' ? (
            <XiaohongshuIcon className="w-full h-full" />
          ) : (
            <span className="font-bold text-gray-400">?</span>
          )}
        </div>
        <div>
          <h3 className="font-bold text-gray-900">{account.username}</h3>
          <p className="text-xs text-gray-500 mt-0.5 capitalize">
            {account.platform === 'xiaohongshu' ? '小红书' : account.platform}
          </p>
        </div>
      </div>
      <div className={`px-2 py-1 rounded text-xs font-medium flex items-center ${account.isActive ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
        {account.isActive ? <CheckCircle size={12} className="mr-1" /> : <XCircle size={12} className="mr-1" />}
        {account.isActive ? '活跃' : '失效'}
      </div>
    </div>

    <div className="space-y-2 text-xs text-gray-500 mb-6">
      <div className="flex justify-between">
        <span>连接时间:</span>
        <span className="font-mono">{new Date(account.createdAt).toLocaleDateString()}</span>
      </div>
      <div className="flex justify-between">
        <span>发布状态:</span>
        <span>{account.isActive ? '正常' : '需重新授权'}</span>
      </div>
    </div>

    <div className="flex space-x-2 pt-4 border-t border-gray-100">
      <button 
        onClick={() => onEdit(account)}
        className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium flex items-center justify-center transition-colors"
      >
        <Key size={14} className="mr-2" />
        修改密码
      </button>
      <button 
        onClick={() => onDelete(account.id)}
        className="p-2 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-100 hover:bg-red-50 transition-colors"
      >
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

const AddAccountModal = ({ onClose, onSave, editingAccount }) => {
  const [formData, setFormData] = useState({
    username: editingAccount?.username || '',
    password: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...formData, id: editingAccount?.id });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-lg font-bold text-gray-900">
            {editingAccount ? '修改账户密码' : '添加发布账号'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">选择平台</label>
            <div className="grid grid-cols-2 gap-3">
                <div className="border border-gray-200 bg-gray-50 text-gray-400 rounded-xl p-3 flex items-center justify-center cursor-not-allowed text-sm grayscale opacity-60" title="暂未开放">
                    <XiaohongshuIcon className="w-5 h-5 mr-2" />
                    小红书 (暂未开放)
                </div>
                <div className="border border-gray-200 bg-gray-50 text-gray-400 rounded-xl p-3 flex items-center justify-center cursor-not-allowed text-sm">
                    <DouyinIcon className="w-5 h-5 mr-2 grayscale opacity-50" />
                    抖音 (即将上线)
                </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">登录用户名/手机号</label>
            <input 
              type="text" 
              required
              disabled={!!editingAccount}
              value={formData.username}
              onChange={e => setFormData({...formData, username: e.target.value})}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="请输入小红书登录账号"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {editingAccount ? '新密码' : '登录密码'}
            </label>
            <input 
              type="password" 
              required={!editingAccount}
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder={editingAccount ? "留空则不修改" : "请输入登录密码"}
            />
            <p className="text-xs text-gray-400 mt-2 flex items-center">
                <span className="w-3 h-3 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-1 text-[10px]">i</span>
                密码仅用于本地模拟登录，系统会对数据进行加密存储。
            </p>
          </div>

          <div className="pt-4 flex justify-end space-x-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">取消</button>
            <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-lg shadow-blue-200">
                {editingAccount ? '保存修改' : '确认添加'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Accounts() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  useEffect(() => {
    if (user) fetchAccounts();
  }, [user]);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/accounts`, {
        headers: { 'x-user-email': user.email }
      });
      setAccounts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("确定要删除这个账号吗？删除后将无法自动发布。")) return;
    try {
        await axios.delete(`${API_URL}/accounts/${id}`, {
            headers: { 'x-user-email': user.email }
        });
        fetchAccounts();
    } catch (err) {
        alert("删除失败");
    }
  };

  const handleSave = async (data) => {
    try {
        if (data.id) {
            // Edit Password
            if (data.password) {
                await axios.patch(`${API_URL}/accounts/${data.id}`, { 
                    token: data.password, // Update token
                    isActive: true // Reactivate if was expired
                }, {
                    headers: { 'x-user-email': user.email }
                });
            }
        } else {
            // Create
            await axios.post(`${API_URL}/accounts`, {
                platform: 'xiaohongshu',
                username: data.username,
                token: data.password
            }, {
                headers: { 'x-user-email': user.email }
            });
        }
        setShowModal(false);
        setEditingAccount(null);
        fetchAccounts();
    } catch (err) {
        alert("操作失败");
    }
  };

  const openAdd = () => {
      setEditingAccount(null);
      setShowModal(true);
  };

  const openEdit = (account) => {
      setEditingAccount(account);
      setShowModal(true);
  };

  if (loading) return <div className="p-10 text-center text-gray-500">加载账号列表...</div>;

  return (
    <div className="max-w-5xl mx-auto pb-20">
      <header className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">发布账号管理</h1>
          <p className="text-gray-500 mt-1">管理您的小红书及其他平台账号，实现一键分发。</p>
        </div>
        <button 
            onClick={openAdd}
            className="flex items-center space-x-2 bg-gray-900 text-white px-4 py-2.5 rounded-xl font-medium shadow-lg hover:bg-black transition-transform hover:-translate-y-0.5"
        >
          <Plus size={18} />
          <span>添加新账号</span>
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map(account => (
            <AccountCard 
                key={account.id} 
                account={account} 
                onEdit={openEdit}
                onDelete={handleDelete}
            />
        ))}
        
        {/* Empty State / Add Placeholder */}
        <div 
            onClick={openAdd}
            className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer min-h-[200px] group"
        >
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-500 transition-colors">
                <Plus size={24} />
            </div>
            <h3 className="font-medium text-gray-900 group-hover:text-blue-600">连接新账号</h3>
            <p className="text-xs text-gray-400 mt-1">支持小红书 (即将支持抖音)</p>
        </div>
      </div>

      {showModal && (
          <AddAccountModal 
            onClose={() => setShowModal(false)} 
            onSave={handleSave} 
            editingAccount={editingAccount} 
          />
      )}
    </div>
  );
}
